import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './VideoGenerationModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoGenerationModal = ({ isOpen, onClose, resultImage, onVideoGenerated }) => {
  const { sessionToken, refreshUser } = useAuth();
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('720p');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const handleGenerate = async () => {
    if (!sessionToken) {
      setError('Please login to generate videos');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setVideoUrl(null);

      const formData = new FormData();
      formData.append('result_image', resultImage);
      formData.append('duration', duration);
      formData.append('resolution', resolution);
      formData.append('prompt', prompt);

      const response = await axios.post(
        `${API}/video/generate`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setVideoUrl(response.data.video_url);
        
        // Refresh user credits
        await refreshUser();
        
        // Notify parent component
        if (onVideoGenerated) {
          onVideoGenerated(response.data);
        }
      }

    } catch (err) {
      console.error('Video generation error:', err);
      
      if (err.response?.status === 402) {
        setError(err.response.data.detail || 'Insufficient credits');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again');
      } else if (err.response?.status === 408) {
        setError('Video generation is taking longer than expected. Please try again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to generate video');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `tryon-video-${Date.now()}.mp4`;
      link.target = '_blank';
      link.click();
    }
  };

  const handleClose = () => {
    setVideoUrl(null);
    setError(null);
    setPrompt('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="video-modal-overlay" onClick={handleClose}>
      <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="video-modal-header">
          <h2>🎬 Generate Video</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        {!videoUrl ? (
          <div className="video-modal-body">
            <div className="preview-section">
              <h3>Try-On Result</h3>
              <img src={resultImage} alt="Try-on result" className="result-preview" />
            </div>

            <div className="settings-section">
              <h3>Video Settings</h3>
              
              <div className="setting-group">
                <label>Duration</label>
                <div className="duration-buttons">
                  <button
                    className={`duration-btn ${duration === 5 ? 'active' : ''}`}
                    onClick={() => setDuration(5)}
                  >
                    5 seconds
                  </button>
                  <button
                    className={`duration-btn ${duration === 10 ? 'active' : ''}`}
                    onClick={() => setDuration(10)}
                  >
                    10 seconds
                  </button>
                </div>
              </div>

              <div className="setting-group">
                <label>Resolution</label>
                <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  <option value="480p">480p (Fast)</option>
                  <option value="720p">720p (Balanced)</option>
                  <option value="1080p">1080p (High Quality)</option>
                </select>
              </div>

              <div className="setting-group">
                <label>Motion Prompt (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g., raising hand to touch face, smiling"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  maxLength={100}
                />
                <p className="hint">Leave empty for automatic motion</p>
              </div>

              <div className="credits-info">
                <span className="credits-badge">💎 2 Credits</span>
                <span className="info-text">Premium video generation</span>
              </div>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="generate-btn"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Generating... (may take up to 60s)
                  </>
                ) : (
                  '🎬 Generate Video'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="video-result-section">
            <h3>✅ Video Generated Successfully!</h3>
            
            <div className="video-container">
              <video controls autoPlay loop>
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="result-actions">
              <button className="download-btn" onClick={handleDownload}>
                📥 Download Video
              </button>
              <button className="close-btn-alt" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoGenerationModal;
